import Checked from '@/assets/icons/Checked'
import CreditCircle from '@/assets/icons/CreditCircle'
import HomeCircle from '@/assets/icons/HomeCircle'
import NotificationCircle from '@/assets/icons/NotificationCircle'
import SecurityCircle from '@/assets/icons/SecurityCircle'
import Unchecked from '@/assets/icons/Unchecked'
import UserCircle from '@/assets/icons/UserCircle'
import { useAuth } from '@/auth/Auth'
import Loader from '@/components/Loader/Loader'
import { AddressStep } from '@/components/Onboarding/Steps/AddressStep'
import { BankStep } from '@/components/Onboarding/Steps/BankStep'
import { DocumentStep } from '@/components/Onboarding/Steps/DocumentStep'
import { NotificationStep } from '@/components/Onboarding/Steps/NotificationStep'
import { ProfileStep } from '@/components/Onboarding/Steps/ProfileStep'
import { SecurityStep } from '@/components/Onboarding/Steps/SecurityStep'
import Title from '@/components/shared/Title/Title'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'
import { AcquirerStep } from '@/components/Onboarding/Steps/AcquirerStep'
import LayoutInside from '@/layout/LayoutInside'
import { cn, formatDateToMonthYear, userInitials } from '@/lib/utils'
import { AuthService } from '@/services/auth.service'
import { SellerContractService } from '@/services/seller-contract.service'
import { NotificationsService } from '@/services/notifications.service'
import { ProfileService } from '@/services/profile.service'
import useModalStore from '@/store/useModalStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import VeriffSDK from '@veriff/js-sdk'
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Calendar,
  CircleAlert,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { downloadBlobAsFile } from '@/utils/downloadBlobAsFile'
import { isProfileRejectedByUserInfo } from '@/utils/onboardingIdentityVerification'
import { usePermissions } from '@/auth/PermissionsContext'
import { IProfile } from '@/@types/Onboarding'
const Veriff = (VeriffSDK as any).Veriff || VeriffSDK

export default function MyAccount() {
  const { modals, openModal, closeModal } = useModalStore()
  const { authData } = useAuth()
  const { t, i18n } = useTranslation(['onboarding', 'common'])
  const queryClient = useQueryClient()
  const { hasPermission, isPrimary } = usePermissions()
  const [contractDownloadLocale, setContractDownloadLocale] = useState<
    null | 'pt' | 'en'
  >(null)

  const canViewProfile = hasPermission('account.view_profile_identity')
  const canEditProfile = hasPermission('account.edit_profile_identity')
  const canViewAddress = hasPermission('account.view_address')
  const canEditAddress = hasPermission('account.edit_address')
  const canViewBankAccount =
    isPrimary && hasPermission('account.view_bank_account')
  const canViewNotifications = hasPermission('account.view_notification_settings')
  const canEditNotifications = hasPermission('account.edit_notification_settings')

  const isWideCardLocale = useMemo(() => {
    const base = (i18n.language ?? 'en').split('-')[0]?.toLowerCase()
    return base === 'fr' || base === 'de'
  }, [i18n.language])

  const navigate = useNavigate()
  const routeTo =
    authData?.onboarding_step === 'finished' &&
      (authData?.kyc_status === 'approved' || authData?.has_acquirer)
      ? '/my-account'
      : '/onboarding'

  const { data: profile, isPending: isLoadingProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => await ProfileService.getProfile(),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const { data: userInfo, isPending: isLoadingUserInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
    staleTime: 1000 * 60 * 60 * 24,
  })

  useQuery({
    queryKey: ['notification-settings'],
    queryFn: NotificationsService.getSettings,
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    // Se o usuário ainda não enviou documentação, não deve acessar "Minha conta"
    if (isLoadingUserInfo) return
    const isOnboardingFinished =
      userInfo?.onboarding_step === 'finished' ||
      authData?.onboarding_step === 'finished'

    const isDocumentRejected = userInfo?.documentation_status === 'disapproved'

    // Se já concluiu o onboarding (ou se o documento foi reprovado), não redireciona
    // para permitir que o usuário veja/reenviar documentos pelo My Account.
    if (isOnboardingFinished || isDocumentRejected) return

    if (userInfo?.onboarding_detail?.document === false) {
      navigate('/onboarding', { replace: true })
    }
  }, [
    authData?.onboarding_step,
    isLoadingUserInfo,
    userInfo?.documentation_status,
    userInfo?.onboarding_detail?.document,
    userInfo?.onboarding_step,
    navigate,
  ])

  const breadcrumbItems = [{ text: t('my_account'), link: routeTo }]

  const downloadSignedSellerPdf = useCallback(
    async (kind: 'contract' | 'receipt', locale: 'pt' | 'en') => {
      setContractDownloadLocale(locale)
      try {
        const current = await queryClient.ensureQueryData({
          queryKey: ['seller-contract', 'current'],
          queryFn: () => SellerContractService.getCurrent(),
        })
        const version = current?.contract_version
        if (!version) {
          toast({
            variant: 'destructive',
            description: t('contract_data_error'),
          })
          return
        }

        const filename =
          kind === 'receipt'
            ? `comprovante-assinatura-${version}.pdf`
            : `contrato-assinado-${version}.pdf`

        const blob = await SellerContractService.fetchSignedContractBlob(
          current,
          { kind, locale },
        )

        downloadBlobAsFile(blob, filename)
      } catch {
        toast({
          variant: 'destructive',
          description: t('contract_data_error'),
        })
      } finally {
        setContractDownloadLocale(null)
      }
    },
    [queryClient, t],
  )

  const handleGoToContract = useCallback(() => {
    const returnTo = '/my-account'
    navigate(`/contract?returnTo=${encodeURIComponent(returnTo)}`)
  }, [navigate])

  const handleStartVeriff = async () => {
    try {
      const veriff = Veriff({
        apiKey: import.meta.env.VITE_VERIFF_API_KEY,
        parentId: 'veriff-root',
        onSession: function (
          err: { status?: { text?: string } } | null,
          response: { verification?: { url?: string } },
        ) {
          if (err) {
            console.error('Veriff session error:', err)
            toast({
              variant: 'error',
              description: err.status?.text ?? t('veriff_initialization_error'),
            })
            return
          }
          if (response.verification?.url) {
            window.location.href = response.verification.url
          }
        },
      })

      veriff.setParams({
        person: {
          givenName: ' ',
          lastName: ' ',
        },
        vendorData: userInfo?.id,
      })

      veriff.mount({
        submitBtnText: t('start_verification'),
      })
    } catch (error) {
      console.error('Veriff initialization error:', error)
    }
  }

  type MainInfoStep = {
    id: number
    title: string
    onClick: () => void
    icon: ReactNode
    disabled: boolean
  }

  const mainInfoSteps: MainInfoStep[] = []

  if (canViewProfile) {
    mainInfoSteps.push({
      id: 1,
      title: t('profile_identity'),
      onClick: () => openModal('profile'),
      icon: <UserCircle />,
      disabled: false,
    })
  }
  if (canViewAddress) {
    mainInfoSteps.push({
      id: 2,
      title: t('address'),
      onClick: () => openModal('address'),
      icon: <HomeCircle />,
      disabled: false,
    })
  }
  if (canViewBankAccount) {
    mainInfoSteps.push({
      id: 3,
      title: t('bank_account'),
      onClick: () => openModal('bank'),
      icon: <CreditCircle />,
      disabled: false,
    })
  }
  if (canViewNotifications) {
    mainInfoSteps.push({
      id: 4,
      title: t('notifications'),
      onClick: () => openModal('notifications'),
      icon: <NotificationCircle />,
      disabled: false,
    })
  }
  if (isPrimary) {
    mainInfoSteps.push({
      id: 5,
      title: t('security'),
      onClick: () => openModal('security'),
      icon: <SecurityCircle />,
      disabled: false,
    })
  }

  return (
    <LayoutInside items={breadcrumbItems}>
      {canViewProfile && (
        <ProfileStep
          isPending={Boolean(userInfo?.onboarding_detail?.profile)}
          onClose={() => closeModal('profile')}
          open={modals['profile']}
          profile={profile ?? null}
          isEditing
          rejectionReason={userInfo?.onboarding_rejection_reason}
          isProfileRejected={isProfileRejectedByUserInfo(userInfo)}
          canEdit={canEditProfile}
        />
      )}

      {canViewAddress && (
        <AddressStep
          isPending={Boolean(userInfo?.onboarding_detail?.address)}
          open={modals['address']}
          onClose={() => closeModal('address')}
          isEditing
          canEdit={canEditAddress}
        />
      )}

      {canViewBankAccount && (
        <BankStep
          key={String(authData?.id ?? '')}
          isPending={Boolean(userInfo?.onboarding_detail?.bankAccount)}
          open={modals['bank']}
          onClose={() => closeModal('bank')}
          isEditing
          isOnboarding={false}
          initialCurrency={'BRL'}
          canEdit={isPrimary}
        />
      )}

      <DocumentStep
        isPending={userInfo?.documentation_status === 'disapproved'}
        documentType={profile?.profile_type}
        open={modals['documents']}
        onClose={() => closeModal('documents')}
        isEditing
      />

      {canViewNotifications && (
        <NotificationStep
          open={modals['notifications']}
          onClose={() => closeModal('notifications')}
          canEdit={canEditNotifications}
        />
      )}

      {isPrimary && (
        <SecurityStep
          open={modals['security']}
          onClose={() => closeModal('security')}
          loginTwoFactorEnabled={Boolean(
            userInfo?.account?.login_two_factor_enabled,
          )}
        />
      )}

      {isLoadingProfile || isLoadingUserInfo ? (
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <Loader />
        </div>
      ) : (
        <section className="flex flex-col items-center">
          <Avatar className="h-16 w-16 bg-blackA1">
            <AvatarImage className="h-full w-full" alt="@shadcn" />
            <AvatarFallback className="bg-elevated text-xl font-medium text-muted">
              {userInitials(userInfo?.name ?? '')}
            </AvatarFallback>
          </Avatar>
          <Title className="mt-4 !text-xl" text={userInfo?.name ?? ''} />
          <p className="text-muted lg:w-[300px] mt-1 text-sm text-center">
            {userInfo?.email}
          </p>
          <Card className={cn('mt-8 w-full bg-bgDark lg:w-[504px] rounded-xl')}>
            {userInfo?.documentation_status === 'disapproved' && (
              <CardHeader className="px-4 py-3 border-b">
                <div className="flex justify-between items-center max-[420px]:flex-col max-[420px]:gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-500/20 h-8 w-8 rounded-full flex items-center justify-center">
                      <CircleAlert className="h-4 w-4 text-red-500" />
                    </div>
                    <h3 className="text-base">{t('documents')}</h3>
                    <Badge variant="destructive" className="bg-red-500/20">
                      <span className="text-red-500 font-normal">
                        {t('failed')}
                      </span>
                    </Badge>
                  </div>
                  <Button
                    variant="secondary"
                    className="max-[420px]:w-full"
                    onClick={() => openModal('documents')}
                  >
                    {t('new_submission')}
                  </Button>
                </div>
              </CardHeader>
            )}
            <CardContent className="px-4  py-2 space-y-2">
              {mainInfoSteps.map((step) => (
                <li key={step?.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-4 h-14">
                    {step.icon}
                    <p className="font-medium">{step.title ?? ''}</p>
                    {step.disabled && (
                      <p className="text-muted">{`(${t(
                        'common:coming_soon',
                      )})`}</p>
                    )}
                  </div>
                  <Button
                    onClick={step.onClick}
                    variant="outline"
                    size="icon"
                    className="h-9 w-12"
                    disabled={step.disabled}
                  >
                    <ArrowRight />
                  </Button>
                </li>
              ))}

              {/* Verificação adicional de recebedor. Vive aqui além da tela de onboarding porque
                  /onboarding redireciona para cá assim que o cadastro é concluído — quem já
                  terminou nunca mais veria o passo do outro lado. */}
              <AcquirerStep />

              {isPrimary && (
                <div className="w-full">
                  <li className="flex items-center justify-between">
                    <div className="flex items-center gap-4 h-14">
                      {userInfo?.kyc_status === 'approved' ? (
                        <Checked />
                      ) : (
                        <Unchecked className="text-red-500" />
                      )}
                      <p className="font-medium">{t('identity_verification')}</p>
                    </div>
                    <Button
                      onClick={handleStartVeriff}
                      variant="outline"
                      size="icon"
                      className="h-9 w-12"
                      disabled={userInfo?.kyc_status === 'approved'}
                    >
                      <ArrowRight />
                    </Button>
                  </li>

                  <li className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-4 h-14">
                      {userInfo?.contract_signed === true ? (
                        <Checked />
                      ) : (
                        <Unchecked className="text-red-500" />
                      )}
                      <p className="font-medium">
                        {t('onboarding:download_contract')}
                      </p>
                    </div>
                    {userInfo?.contract_signed === true ? (
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 min-w-[5.5rem]"
                          disabled={contractDownloadLocale !== null}
                          onClick={() =>
                            void downloadSignedSellerPdf('contract', 'pt')
                          }
                        >
                          {contractDownloadLocale === 'pt' ? (
                            <Loader2
                              className="h-4 w-4 shrink-0 animate-spin"
                              aria-hidden
                            />
                          ) : null}
                          {t(`common:languages.pt`)}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 min-w-[5.5rem]"
                          disabled={contractDownloadLocale !== null}
                          onClick={() =>
                            void downloadSignedSellerPdf('contract', 'en')
                          }
                        >
                          {contractDownloadLocale === 'en' ? (
                            <Loader2
                              className="h-4 w-4 shrink-0 animate-spin"
                              aria-hidden
                            />
                          ) : null}
                          {t(`common:languages.en`)}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={handleGoToContract}
                      >
                        {t('go_sign_contract')}
                      </Button>
                    )}
                  </li>

                  <div className="w-full flex items-center justify-center m-auto">
                    <div
                      id="veriff-root"
                      className="flex items-center justify-center"
                    ></div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="w-full gap-4 flex justify-center items-center border-t border-t-colorBorder p-4">
              {userInfo?.documentation_status === 'analyzing' ? (
                <Badge
                  variant="secondary"
                  className="text-sm font-medium gap-2 flex items-center justify-center"
                >
                  <AlertCircle size={14} color="#FACC15" />
                  {t('documents_under_review')}
                </Badge>
              ) : userInfo?.documentation_status === 'approved' ? (
                <Badge
                  variant="secondary"
                  className="text-sm font-medium gap-2 flex items-center justify-center"
                >
                  <BadgeCheck size={14} color="#A9D342" />
                  {t('verified_account')}
                </Badge>
              ) : (
                <></>
              )}
              <Badge
                variant="secondary"
                className="text-sm font-medium gap-2 flex items-center justify-center"
              >
                <Calendar color="#A1A1AA" size={16} />
                {t('member_since')},{' '}
                {formatDateToMonthYear(userInfo?.created_at ?? '')}
              </Badge>
            </CardFooter>
          </Card>
        </section>
      )}
    </LayoutInside>
  )
}
